import type {
  Decision,
  SubmissionStage,
  SubmissionStatus,
  SubmissionType,
} from './submissions.js';

/**
 * Approval state machines for each intake type. A transition takes the current
 * stage plus a decision and returns the next stage, or `null` if the decision
 * is not valid at that stage.
 *
 *  Trade / Supplier (single-stage):
 *    submitted --approve--> completed
 *    submitted --decline--> declined
 *
 *  Credit (two-stage):
 *    submitted      --approve--> pending_credit   (CS; iQ Lead created, "credit in review" email)
 *    submitted      --decline--> declined          (CS; no credit stage)
 *    pending_credit --approve--> completed          (Accounts; "credit approved", post terms to iQ)
 *    pending_credit --decline--> credit_declined    (Accounts; trade account stays)
 *
 *  Lead: no approval — created directly at `completed`.
 */
export function transition(
  type: SubmissionType,
  stage: SubmissionStage,
  decision: Decision,
): SubmissionStage | null {
  switch (type) {
    case 'trade_account':
    case 'supplier_account':
      if (stage === 'submitted') {
        return decision === 'approve' ? 'completed' : 'declined';
      }
      return null;

    case 'credit_account':
      if (stage === 'submitted') {
        return decision === 'approve' ? 'pending_credit' : 'declined';
      }
      if (stage === 'pending_credit') {
        return decision === 'approve' ? 'completed' : 'credit_declined';
      }
      return null;

    case 'lead':
      return null;
  }
}

/** Stages where a staff decision is expected. */
export function isActionable(
  type: SubmissionType,
  stage: SubmissionStage,
): boolean {
  if (type === 'lead') return false;
  if (stage === 'submitted') return true;
  if (type === 'credit_account' && stage === 'pending_credit') return true;
  return false;
}

/** Derive the inbox display bucket from the workflow stage. */
export function statusForStage(
  type: SubmissionType,
  stage: SubmissionStage,
): SubmissionStatus {
  if (type === 'lead') return 'lead';
  switch (stage) {
    case 'submitted':
      return 'new';
    case 'pending_credit':
      return 'pending';
    case 'completed':
      return 'approved';
    case 'declined':
    case 'credit_declined':
      return 'declined';
  }
}

/** The Teams approval body acting at a stage. */
export function approverFor(
  type: SubmissionType,
  stage: SubmissionStage,
): 'cs' | 'accounts' | 'supplier' | null {
  if (type === 'supplier_account' && stage === 'submitted') return 'supplier';
  if (type === 'credit_account' && stage === 'pending_credit') return 'accounts';
  if (
    (type === 'trade_account' || type === 'credit_account') &&
    stage === 'submitted'
  ) {
    return 'cs';
  }
  return null;
}

export interface StepDef {
  key: string;
  label: string;
}

/** Stepper definitions for the submission detail view, per type. */
export const STEPPERS: Record<SubmissionType, StepDef[]> = {
  trade_account: [
    { key: 'submitted', label: 'Received' },
    { key: 'cs', label: 'Customer Service' },
    { key: 'active', label: 'Account set up' },
  ],
  supplier_account: [
    { key: 'submitted', label: 'Received' },
    { key: 'supplier', label: 'Supplier team' },
    { key: 'active', label: 'Supplier set up' },
  ],
  credit_account: [
    { key: 'submitted', label: 'Received' },
    { key: 'cs', label: 'Customer Service' },
    { key: 'accounts', label: 'Accounts (credit)' },
    { key: 'active', label: 'Credit terms set' },
  ],
  lead: [
    { key: 'received', label: 'Received' },
    { key: 'iq', label: 'Added to iQ' },
  ],
};
